/**
 * API client for planning runs (Stressszenarien on archive basis):
 * POST /api/zones/{zoneId}/planning/run and the run history.
 */
import { authService } from './authService';
import type {
  PlanningBaseCaseAvailability,
  PlanningCalculationUnit,
  PlanningMeasureCatalog,
  PlanningRemedyResult,
  PlanningRunListItem,
  PlanningRunRequest,
  PlanningRunResult,
} from '../types/planning';
import type { PlanningMeasure } from '../types/scenario';
import type {
  PlanningJobStatus,
  PlanningRolloutDefaults,
  PlanningRolloutRequest,
  PlanningRolloutResult,
} from '../types/planningRollout';

const authHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  const token = authService.getToken();
  const tenantId = authService.getTenantId();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  return headers;
};

const base = (zoneId: string) => `/api/zones/${encodeURIComponent(zoneId)}/planning`;

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    if (body && typeof body.error === 'string') return body.error;
  } catch {
    // no JSON body
  }
  return `${fallback} (${response.status})`;
}

export const planningService = {
  async run(zoneId: string, request: PlanningRunRequest): Promise<PlanningRunResult> {
    const response = await fetch(`${base(zoneId)}/run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Planungsrechnung fehlgeschlagen'));
    }
    return response.json();
  },

  /** The calculation unit (zone or Netzverbund) under the scenario's coupling measures — nothing is solved. */
  async getUnit(zoneId: string, measures: PlanningMeasure[]): Promise<PlanningCalculationUnit> {
    const response = await fetch(`${base(zoneId)}/unit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ measures }),
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Recheneinheit nicht bestimmbar'));
    }
    return response.json();
  },

  /** "Abhilfe finden": runs the request, proposes measures against its limit breaches and evaluates each (nothing persisted). */
  async findRemedies(zoneId: string, request: PlanningRunRequest): Promise<PlanningRemedyResult> {
    const response = await fetch(`${base(zoneId)}/remedies`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Abhilfe-Suche fehlgeschlagen'));
    }
    return response.json();
  },

  async getBaseCase(zoneId: string): Promise<PlanningBaseCaseAvailability> {
    const response = await fetch(`${base(zoneId)}/base-case`, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(await readError(response, 'Archivstatus nicht lesbar'));
    }
    return response.json();
  },

  async listRuns(zoneId: string, limit = 30): Promise<PlanningRunListItem[]> {
    const response = await fetch(`${base(zoneId)}/runs?limit=${limit}`, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(await readError(response, 'Planungsläufe nicht lesbar'));
    }
    return response.json();
  },

  async getRun(zoneId: string, runId: string): Promise<PlanningRunResult> {
    const response = await fetch(`${base(zoneId)}/runs/${runId}`, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(await readError(response, 'Planungslauf nicht lesbar'));
    }
    return response.json();
  },

  async getCatalog(zoneId: string): Promise<PlanningMeasureCatalog> {
    const response = await fetch(`${base(zoneId)}/catalog`, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(await readError(response, 'Maßnahmenkatalog nicht lesbar'));
    }
    return response.json();
  },

  // ── Hochlaufpfad (rollout jobs) ──

  async getRolloutDefaults(zoneId: string): Promise<PlanningRolloutDefaults> {
    const response = await fetch(`${base(zoneId)}/rollout/defaults`, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(await readError(response, 'Hochlauf-Vorgaben nicht lesbar'));
    }
    return response.json();
  },

  async startRollout(zoneId: string, request: PlanningRolloutRequest): Promise<PlanningJobStatus> {
    const response = await fetch(`${base(zoneId)}/rollout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error(await readError(response, 'Hochlauf konnte nicht gestartet werden'));
    }
    return response.json();
  },

  async getRolloutJob(zoneId: string, jobId: string): Promise<PlanningJobStatus> {
    const response = await fetch(`${base(zoneId)}/rollout/jobs/${jobId}`, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(await readError(response, 'Hochlauf-Job nicht lesbar'));
    }
    return response.json();
  },

  async getRolloutRun(zoneId: string, runId: string): Promise<PlanningRolloutResult> {
    const response = await fetch(`${base(zoneId)}/rollout/runs/${runId}`, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(await readError(response, 'Hochlauf-Lauf nicht lesbar'));
    }
    return response.json();
  },

  async deleteRun(zoneId: string, runId: string): Promise<void> {
    const response = await fetch(`${base(zoneId)}/runs/${runId}`, { method: 'DELETE', headers: authHeaders() });
    if (!response.ok && response.status !== 404) {
      throw new Error(await readError(response, 'Planungslauf konnte nicht gelöscht werden'));
    }
  },
};
